import type { SyncDiagnosticResult } from './syncDiagnosticEvents';

type ContextEvent = { message?: string; level: string; timestamp?: number; occurredAt?: string };

/** Classifica a falha do sync; erros paralelos da interface permanecem apenas no contexto. */
export const classifySyncDiagnosticFailure = (
  result: SyncDiagnosticResult,
  events: readonly ContextEvent[],
) => {
  const messages = events.filter(event => {
    const trace = /\[trace:([^\]]+)\]/.exec(event.message ?? '')?.[1];
    if (trace && trace !== result.traceId) return false;
    const at = event.timestamp ?? Date.parse(event.occurredAt ?? '');
    return at >= result.startedAt && (event.level === 'error' || event.level === 'warn') &&
      /\[SYNC:[A-Z_-]+\]/.test(event.message ?? '');
  }).map(event => event.message ?? '');
  const primary = classifyMessage(`${result.error} ${JSON.stringify(result.exception ?? {})}`);
  const sqliteFailure = result.sqliteOperations?.some(operation => operation.outcome === 'failure' && operation.errorClass !== 'other');
  const context = sqliteFailure ? { failure: 'local' as const, errorCode: 'database_locked' } : classifyMessage(messages.join('\n'));
  // Rede não pode adiar a captura de falha local ou recusa explícita do próprio sync.
  if (primary?.failure === 'network' && context && context.failure !== 'network') return context;
  return primary ?? context ?? { failure: 'other' as const, errorCode: 'sync_failed' };
};

const classifyMessage = (message: string) => {
  const locked = /database (?:table |schema )?is locked|sqlite_(?:busy|locked)/i.test(message);
  const local = locked || /sqliteerror|constraint failed|disk.*full|no space left|local_source_missing/i.test(message);
  // Uma recusa explícita do servidor não deve ser reclassificada por um aviso de rede concomitante.
  const rejected = /42501|row.level security|P0002|23[0-9A-Z]{3}/i.test(message);
  const network = !rejected && /timeout|timed out|network request failed|failed to fetch|fetch failed|UnknownHostException|Unable to resolve host|No address associated with hostname|offline|internet|disconnected/i.test(message);
  if (!local && !network && !rejected) return null;
  return {
    failure: local ? 'local' as const : network ? 'network' as const : 'other' as const,
    errorCode: locked ? 'database_locked' : local ? 'local_failure' : network ? 'network' : 'sync_failed',
  };
};
